import { createReducer } from '@reduxjs/toolkit';
import {
  fetchCodersForQueue,
  addCoderToQueue,
  removeCoderFromQueue,
  openModal,
  closeModal,
  stageAddCoder,
  stageRemoveCoder,
  cancelChanges,
  CoderRecord
} from '../actions/manage-coders.action';

export interface ManageCodersState {
  isOpen: boolean;
  activeQueueId: string | null;
  activeQueueName: string;
  originalAssigned: CoderRecord[];
  stagedAssigned: CoderRecord[];
  availableCoders: CoderRecord[];
  isFetching: boolean;
  isSaving: boolean;
  fetchError: string | null;
  saveError: string | null;
}

const initialState: ManageCodersState = {
  isOpen: false,
  activeQueueId: null,
  activeQueueName: '',
  originalAssigned: [],
  stagedAssigned: [],
  availableCoders: [],
  isFetching: false,
  isSaving: false,
  fetchError: null,
  saveError: null
};

const setsAreEqual = (a: CoderRecord[], b: CoderRecord[]): boolean => {
  if (a.length !== b.length) return false;
  const bIds = new Set(b.map(c => c.coderId));
  return a.every(c => bIds.has(c.coderId));
};

const reducer = createReducer(initialState, builder => {
  builder.addCase(openModal, (state, action) => {
    state.isOpen = true;
    state.activeQueueId = action.payload.queueId;
    state.activeQueueName = action.payload.queueName;
    state.originalAssigned = [];
    state.stagedAssigned = [];
    state.availableCoders = [];
    state.isSaving = false;
    state.fetchError = null;
    state.saveError = null;
  });

  builder.addCase(closeModal, state => {
    state.isOpen = false;
    state.activeQueueId = null;
    state.activeQueueName = '';
    state.originalAssigned = [];
    state.stagedAssigned = [];
    state.availableCoders = [];
    // A successful save dispatches closeModal without a per-call fulfilled handler, so isSaving
    // (set true on add/remove pending) must be cleared here or the Save button stays disabled on reopen.
    state.isSaving = false;
    state.fetchError = null;
    state.saveError = null;
  });

  builder.addCase(cancelChanges, state => {
    state.stagedAssigned = [...state.originalAssigned];
    state.saveError = null;
  });

  builder.addCase(fetchCodersForQueue.pending, state => {
    state.isFetching = true;
    state.fetchError = null;
  });

  builder.addCase(fetchCodersForQueue.fulfilled, (state, action) => {
    state.isFetching = false;
    state.originalAssigned = action.payload.assignedCoders;
    state.stagedAssigned = action.payload.assignedCoders;
    state.availableCoders = action.payload.availableCoders;
    // One query failed but the other succeeded — keep the lists we did get and
    // surface a non-blocking error rather than leaving the modal silently empty.
    state.fetchError = action.payload.fetchError
      ? 'Some coders could not be loaded. The list may be incomplete.'
      : null;
  });

  builder.addCase(fetchCodersForQueue.rejected, (state, action) => {
    state.isFetching = false;
    state.fetchError = action.error.message ?? 'Failed to load coders';
  });

  builder.addCase(stageAddCoder, (state, action) => {
    const { coderId } = action.payload;
    const coder = state.availableCoders.find(c => c.coderId === coderId);
    if (!coder) return;
    state.stagedAssigned = [...state.stagedAssigned, coder];
    state.availableCoders = state.availableCoders.filter(c => c.coderId !== coderId);
  });

  builder.addCase(stageRemoveCoder, (state, action) => {
    const { coderId } = action.payload;
    const coder = state.stagedAssigned.find(c => c.coderId === coderId);
    if (!coder) return;
    state.stagedAssigned = state.stagedAssigned.filter(c => c.coderId !== coderId);
    state.availableCoders = [...state.availableCoders, coder];
  });

  builder.addCase(addCoderToQueue.pending, state => {
    state.isSaving = true;
    state.saveError = null;
  });

  builder.addCase(addCoderToQueue.rejected, (state, action) => {
    state.isSaving = false;
    state.saveError = action.error.message ?? 'Failed to save coder assignment';
  });

  builder.addCase(removeCoderFromQueue.pending, state => {
    state.isSaving = true;
    state.saveError = null;
  });

  builder.addCase(removeCoderFromQueue.rejected, (state, action) => {
    state.isSaving = false;
    state.saveError = action.error.message ?? 'Failed to save coder assignment';
  });

  // Save completion is handled in the container thunk after all API calls finish.
  // Individual add/remove fulfilled cases do not reset isSaving so the container
  // thunk can coordinate multiple API calls then dispatch closeModal.
});

// Selector helper — exported for container use
export const selectManageCoders = (state: any): ManageCodersState =>
  state.manageCoders;

export const selectHasCoderChanges = (state: ManageCodersState): boolean =>
  !setsAreEqual(state.stagedAssigned, state.originalAssigned);

export default reducer;
